import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../utils/api';
import { Brain, Mail, Lock, User, ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

export const UserLogin = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: ''
  });
  
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await api.auth.login(formData.email, formData.password);
        toast.success('Acceso concedido. Bienvenido al Nexus.');
        navigate('/panel');
      } else {
        await api.auth.register(formData.email, formData.password, { fullName: formData.fullName });
        toast.success('Cuenta creada. Ya puedes iniciar sesión.');
        setIsLogin(true);
      }
    } catch (error) {
      toast.error(error.message || 'Error en la secuencia de autenticación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#C6FF00]/5 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#C6FF00]/5 blur-[120px] pointer-events-none" />
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="text-center mb-12">
          <Link to="/" className="inline-flex items-center gap-3 group mb-8">
            <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center border border-white/10 group-hover:border-[#C6FF00]/50 transition-all shadow-[0_0_30px_rgba(198,255,0,0.1)]">
              <Brain className="text-white group-hover:text-[#C6FF00] transition-colors" size={28} />
            </div>
          </Link>
          <h1 className="text-4xl font-black tracking-tighter uppercase italic text-white mb-4">
            {isLogin ? 'Protocolo de Acceso' : 'Registro de Nodo'}
          </h1>
          <p className="text-white/40 text-sm font-medium tracking-wide uppercase">
            {isLogin ? 'Inicie sesión para acceder a su panel de control' : 'Crea una cuenta para gestionar tus servicios'}
          </p>
        </div>

        <div className="bg-white/[0.02] border border-white/5 backdrop-blur-3xl p-10 rounded-[3rem] shadow-2xl relative overflow-hidden group">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C6FF00]/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700" />
          
          <form onSubmit={handleSubmit} className="space-y-8">
            <AnimatePresence mode="wait">
              {!isLogin && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-2"
                >
                  <label className="text-[10px] font-black uppercase tracking-[0.3em] text-[#C6FF00] ml-2">Nombre Completo</label>
                  <div className="relative group/field">
                    <User className="absolute left-6 top-1/2 -translate-y-1/2 text-white/20 group-focus-within/field:text-[#C6FF00] transition-colors" size={18} />
                    <input
                      required
                      type="text"
                      placeholder="JOHN DOE"
                      value={formData.fullName}
                      onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl py-5 pl-16 pr-6 text-white text-xs font-black uppercase tracking-widest focus:border-[#C6FF00]/40 outline-none transition-all placeholder:text-white/10"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-2 text-left">
              <label className="text-[10px] font-black uppercase tracking-[0.3em] text-white/40 ml-2">Identificador Digital (Email)</label>
              <div className="relative group/field">
                <Mail className="absolute left-6 top-1/2 -translate-y-1/2 text-white/20 group-focus-within/field:text-[#C6FF00] transition-colors" size={18} />
                <input
                  required
                  type="email"
                  placeholder="OPS@BUILDBRAINS.IO"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-5 pl-16 pr-6 text-white text-xs font-black uppercase tracking-widest focus:border-[#C6FF00]/40 outline-none transition-all placeholder:text-white/10"
                />
              </div>
            </div>

            <div className="space-y-2 text-left">
              <label className="text-[10px] font-black uppercase tracking-[0.3em] text-white/40 ml-2">Clave de Encriptación (Password)</label>
              <div className="relative group/field">
                <Lock className="absolute left-6 top-1/2 -translate-y-1/2 text-white/20 group-focus-within/field:text-[#C6FF00] transition-colors" size={18} />
                <input
                  required
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-5 pl-16 pr-6 text-white text-xs font-black uppercase tracking-widest focus:border-[#C6FF00]/40 outline-none transition-all placeholder:text-white/10"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-6 bg-[#C6FF00] text-black rounded-2xl font-black uppercase tracking-[0.3em] text-[10px] flex items-center justify-center gap-3 hover:scale-[1.02] active:scale-[0.98] transition-all shadow-[0_0_50px_rgba(198,255,0,0.15)] disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <>
                  {isLogin ? 'Ejecutar Sesión' : 'Cifrar Cuenta'}
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <div className="mt-10 pt-8 border-t border-white/5 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-white/40 hover:text-white text-[10px] font-black uppercase tracking-[0.2em] transition-colors flex items-center justify-center gap-2 w-full"
            >
              {isLogin ? '¿No tienes nodo asignado? Crea uno aquí' : '¿Ya tienes un núcleo? Inicia sesión'}
            </button>
          </div>
        </div>

        <div className="mt-12 flex items-center justify-center gap-8 opacity-20 group-hover:opacity-40 transition-opacity">
          <div className="flex items-center gap-2">
            <ShieldCheck size={14} className="text-[#C6FF00]" />
            <span className="text-[8px] font-black uppercase tracking-widest">Seguridad AES-256</span>
          </div>
          <div className="w-1 h-1 rounded-full bg-white/40" />
          <span className="text-[8px] font-black uppercase tracking-widest">Sincronización Cloud</span>
        </div>
      </motion.div>
    </div>
  );
};
